import { useMemo, useState } from 'react'
import { sortBy } from 'lodash-es'

import { IColorByValueConfig } from 'app/services/api/colorByValue'
import { IEventTableMeta } from 'app/services/api/datamanagement/eventTableAttributes'

import MapComponent from 'features/Map'

import { createCustomRenderer, createDefaultRenderer } from './Map/Renderers'
import { IDataTableFilterModel } from './Tables/DataTable'
import useParseTableFiltersToStringEffect from './utils/useParseTableFiltersToStringEffect'
import useSystemScopeRoutesEffect from './utils/useSystemScopeRoutesEffect'

interface IMapWrapper {
  currentEventTable?: IEventTableMeta
  colorByValue?: IColorByValueConfig
  appliedTableFilters?: IDataTableFilterModel
}

const MapWrapper = ({
  currentEventTable,
  colorByValue,
  appliedTableFilters
}: IMapWrapper) => {
  const parsedTableFilters =
    useParseTableFiltersToStringEffect(appliedTableFilters)

  const rawSystemScopeRoutesFilter = useSystemScopeRoutesEffect()

  const systemScopeRoutesFilter = useMemo(
    () => rawSystemScopeRoutesFilter,
    [rawSystemScopeRoutesFilter]
  )

  const [showStructureLayer, setShowStructureLayer] = useState(false)

  const memoizedFilterQuery = useMemo(() => {
    let query = systemScopeRoutesFilter

    if (parsedTableFilters) {
      query += ` AND ${parsedTableFilters}`
    }

    return query
  }, [systemScopeRoutesFilter, parsedTableFilters])

  const memoizedRenderer = useMemo(() => {
    if (colorByValue?.selectedAttributeId) {
      const displayAttribute = colorByValue.attributesMap.find(
        (attr) => attr.attributeId === colorByValue.selectedAttributeId
      )

      const displayAttributeColumnName = displayAttribute?.attributeCode

      if (displayAttribute) {
        return createCustomRenderer(
          displayAttribute,
          displayAttributeColumnName,
          currentEventTable?.typeCode
        )
      }
    }

    return createDefaultRenderer(currentEventTable?.typeCode)
  }, [colorByValue, currentEventTable])

  const linearContentFields = useMemo(
    () => [
      {
        fieldName: 'RouteName',
        label: 'Route Name'
      },
      {
        fieldName: 'FromMeasure',
        label: 'From Measure',
        format: {
          digitSeparator: true,
          places: 3
        }
      },
      {
        fieldName: 'ToMeasure',
        label: 'To Measure',
        format: {
          digitSeparator: true,
          places: 3
        }
      },
      {
        fieldName: 'Length',
        label: 'Length',
        format: {
          digitSeparator: true,
          places: 3
        }
      },
      {
        fieldName: 'WallThickness',
        label: 'Wall Thickness',
        format: {
          digitSeparator: true,
          places: 3
        }
      },
      {
        fieldName: 'FromDate',
        label: 'From Date'
      },
      {
        fieldName: 'ToDate',
        label: 'To Date'
      }
    ],
    []
  )

  const pointContentFields = useMemo(
    () => [
      {
        fieldName: 'RouteName',
        label: 'Route Name'
      },
      {
        fieldName: 'Measure',
        label: 'Measure',
        format: {
          digitSeparator: true,
          places: 3
        }
      },
      {
        fieldName: 'FromDate',
        label: 'From Date'
      },
      {
        fieldName: 'WallThickness',
        label: 'Wall Thickness',
        format: {
          digitSeparator: true,
          places: 3
        }
      },
      {
        fieldName: 'ToDate',
        label: 'To Date'
      }
    ],
    []
  )

  const attributes = useMemo(
    () =>
      sortBy(currentEventTable?.eventTableAttributes, [
        (i) => i.name.toLowerCase(),
        (i) => i.name
      ]),
    [currentEventTable]
  )

  const memoizedContentFields = useMemo(() => {
    const fields =
      currentEventTable?.typeCode === 'Point'
        ? [...pointContentFields]
        : [...linearContentFields]

    attributes?.forEach((attr) => {
      fields.push({
        fieldName: attr.code,
        label: attr.name
      })
    })

    return fields
  }, [attributes, currentEventTable, linearContentFields, pointContentFields])

  const handleShowStructureLayerChange = useMemo(
    () => setShowStructureLayer,
    []
  )

  return (
    <div className='h-full'>
      <MapComponent
        renderer={memoizedRenderer}
        featureLayerUrl={`${import.meta.env.LH_TIMP_ARC_SERVER}${currentEventTable?.arcGisUrl}`}
        featureLayerTitle={currentEventTable?.name}
        featureLayerFilterQuery={memoizedFilterQuery}
        contentFields={memoizedContentFields}
        showStructureLayer={showStructureLayer}
        onStructureLayerVisibilityChange={handleShowStructureLayerChange}
      />
    </div>
  )
}

export default MapWrapper
